var SCM = SCM || {};

SCM.WidgetScanner = (function () {
    
    function create(options) {
        const _options = _mergeOptions(options);
        let _logger;
        
        // Defensive logger initialization
        try {
            _logger = SCM.DebugLogger.create('WidgetScanner', { debugMode: _options.debugMode || false });
        } catch(e) {
            _logger = { debug: function(){}, info: function(){}, warn: function(){}, error: function(){} };
        }
        let _observer = null;
        let _observerCallback = null;
        const _cache = {
            lastScanTime: 0,
            widgets: [],
            domSignature: '',
            enabled: _options.enableCaching !== false
        };

        function scan(forceRefresh) {
            _logger.debug('Starting widget scan', { selectors: _options.selectors, excludeTypes: _options.excludeTypes, forceRefresh: !!forceRefresh });
            
            // Check cache if enabled and not forcing refresh
            if (_cache.enabled && !forceRefresh) {
                const currentDomSignature = _generateDomSignature();
                const cacheAge = Date.now() - _cache.lastScanTime;
                
                if (_cache.domSignature === currentDomSignature && cacheAge < 5000) { // 5 second cache
                    _logger.debug('Returning cached widget results', { count: _cache.widgets.length, cacheAge: cacheAge });
                    return _cache.widgets.slice(); // Return copy to prevent external modification
                }
            }
            
            const widgets = [];
            const elements = _findWidgetElements();
            
            for (let i = 0; i < elements.length; i++) {
                const element = elements[i];
                const widget = _createWidgetObject(element);
                
                if (widget && _validateWidget(widget)) {
                    widgets.push(widget);
                }
            }
            
            // Update cache
            if (_cache.enabled) {
                _cache.widgets = widgets.slice(); // Store copy
                _cache.lastScanTime = Date.now();
                _cache.domSignature = _generateDomSignature();
            }
            
            _logger.info('Widget scan completed', { count: widgets.length, cached: _cache.enabled });
            return widgets;
        }

        function observe(callback) {
            if (!callback || typeof callback !== 'function') {
                _logger.error('Invalid callback provided to observe method');
                return;
            }
            
            if (!window.MutationObserver) {
                _logger.warn('MutationObserver not supported, observe functionality disabled');
                return;
            }

            _observerCallback = callback;
            
            _observer = new MutationObserver(function(mutations) {
                let addedWidgets = [];
                let cacheInvalidated = false;
                
                for (let i = 0; i < mutations.length; i++) {
                    const mutation = mutations[i];
                    if (mutation.type === 'childList' && (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)) {
                        if (!cacheInvalidated) {
                            _invalidateCache();
                            cacheInvalidated = true;
                        }
                        if (mutation.addedNodes) {
                            addedWidgets = addedWidgets.concat(_processAddedNodes(mutation.addedNodes));
                        }
                    }
                }
                
                if (addedWidgets.length > 0) {
                    _logger.debug('New widgets detected via observer', { count: addedWidgets.length });
                    _observerCallback(addedWidgets);
                }
            });
            
            _observer.observe(document.body, {
                childList: true,
                subtree: true
            });
            
            _logger.info('Widget observer started');
        }

        function destroy() {
            if (_observer) {
                _observer.disconnect();
                _observer = null;
                _observerCallback = null;
                _logger.info('Widget observer stopped');
            }
        }

        function _findWidgetElements() {
            const elements = [];
            
            for (let i = 0; i < _options.selectors.length; i++) {
                const selector = _options.selectors[i];
                try {
                    const found = document.querySelectorAll(selector);
                    for (let j = 0; j < found.length; j++) {
                        elements.push(found[j]);
                    }
                } catch (error) {
                    _logger.error('Invalid selector', { selector: selector, error: error.message });
                }
            }
            
            return _filterContainerWidgets(elements);
        }

        function _filterContainerWidgets(elements) {
            const filtered = [];
            
            for (let i = 0; i < elements.length; i++) {
                const element = elements[i];
                const widgetName = element.getAttribute('data-widget-name');
                
                if (widgetName && _options.excludeTypes.indexOf(widgetName) === -1) {
                    filtered.push(element);
                } else if (widgetName) {
                    _logger.debug('Filtered out container widget', { widgetName: widgetName });
                }
            }
            
            return filtered;
        }

        function _createWidgetObject(element) {
            try {
                const widgetName = element.getAttribute('data-widget-name');
                const widgetInstance = element.getAttribute('data-widget-instance');
                
                const widget = {
                    name: widgetName,
                    instance: widgetInstance,
                    element: element
                };

                if (_options.extractMetadata && typeof _options.extractMetadata === 'function') {
                    widget.metadata = _options.extractMetadata(element);
                }

                return widget;
            } catch (error) {
                _logger.error('Error creating widget object', { error: error.message });
                return null;
            }
        }

        function _validateWidget(widget) {
            if (!widget.name) {
                _logger.warn('Widget missing name attribute', { widget: widget });
                return false;
            }
            
            if (!widget.element) {
                _logger.warn('Widget missing element reference', { widget: widget });
                return false;
            }
            
            return true;
        }

        function _processAddedNodes(nodes) {
            const addedWidgets = [];
            
            for (let i = 0; i < nodes.length; i++) {
                const node = nodes[i];
                
                if (node.nodeType === Node.ELEMENT_NODE) {
                    // Check if the node itself is a widget
                    if (_isWidgetElement(node)) {
                        const widget = _createWidgetObject(node);
                        if (widget && _validateWidget(widget)) {
                            addedWidgets.push(widget);
                        }
                    }
                    
                    // Check for child widgets
                    const childWidgets = node.querySelectorAll ? _findWidgetElementsInNode(node) : [];
                    for (let j = 0; j < childWidgets.length; j++) {
                        const childWidget = _createWidgetObject(childWidgets[j]);
                        if (childWidget && _validateWidget(childWidget)) {
                            addedWidgets.push(childWidget);
                        }
                    }
                }
            }
            
            return addedWidgets;
        }

        function _isWidgetElement(element) {
            const widgetName = element.getAttribute('data-widget-name');
            return widgetName && _options.excludeTypes.indexOf(widgetName) === -1;
        }

        function _findWidgetElementsInNode(node) {
            const elements = [];
            
            for (let i = 0; i < _options.selectors.length; i++) {
                const selector = _options.selectors[i];
                try {
                    const found = node.querySelectorAll(selector);
                    for (let j = 0; j < found.length; j++) {
                        if (_isWidgetElement(found[j])) {
                            elements.push(found[j]);
                        }
                    }
                } catch (error) {
                    _logger.error('Error finding widgets in node', { selector: selector, error: error.message });
                }
            }
            
            return elements;
        }

        function _generateDomSignature() {
            // Simple DOM signature based on widget count and basic structure
            // This is a lightweight approach to detect DOM changes
            try {
                let elementCount = 0;
                for (let i = 0; i < _options.selectors.length; i++) {
                    const elements = document.querySelectorAll(_options.selectors[i]);
                    elementCount += elements.length;
                }
                return 'widgets:' + elementCount + ':' + document.body.childNodes.length;
            } catch (error) {
                _logger.error('Error generating DOM signature', { error: error.message });
                return Date.now().toString(); // Fallback to timestamp to prevent caching
            }
        }

        function _invalidateCache() {
            _cache.domSignature = '';
            _cache.lastScanTime = 0;
            _logger.debug('Widget cache invalidated');
        }

        function clearCache() {
            _invalidateCache();
            _cache.widgets = [];
        }

        // Return public API
        return {
            scan: scan,
            observe: observe,
            destroy: destroy,
            clearCache: clearCache
        };
    }

    function _mergeOptions(options) {
        const defaults = {
            selectors: ['[data-widget-name]'],
            excludeTypes: ['DynamicWidgetLayout'],
            extractMetadata: null,
            enableCaching: true
        };
        
        const merged = {};
        const source = options || {};
        
        // Copy defaults first
        for (const key in defaults) {
            if (defaults.hasOwnProperty(key)) {
                merged[key] = defaults[key];
            }
        }
        
        // Override with provided options
        for (const key in source) {
            if (source.hasOwnProperty(key)) {
                merged[key] = source[key];
            }
        }
        
        return merged;
    }

    // Return the create function
    return {
        create: create
    };

})();

'object' === typeof module && module.exports && (module.exports = SCM.WidgetScanner);